import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import type { TrialContext } from '@/types/billing';

/**
 * Polls GET /companies/:id/billing/trial every 60s.
 * Returns null until a company is selected.
 */
export function useTrialCountdown() {
  const { selectedCompany, isAuthenticated } = useAuth();
  const companyId = selectedCompany?.id;
  const queryClient = useQueryClient();

  const query = useQuery<TrialContext | null>({
    queryKey: ['billing', 'trial', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      try {
        return await api.billing.getTrial(companyId);
      } catch {
        return null;
      }
    },
    enabled: !!companyId && isAuthenticated,
    refetchInterval: 60_000,
    staleTime: 0,
  });

  useEffect(() => {
    const handler = () => {
      if (!companyId) return;
      queryClient.invalidateQueries({ queryKey: ['billing', 'trial', companyId] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'status', companyId] });
    };
    window.addEventListener('hiromatch:trial-state-changed', handler);
    return () => window.removeEventListener('hiromatch:trial-state-changed', handler);
  }, [companyId, queryClient]);

  return query;
}